/**
 * "FREE" FLAGS (B5 · gap analysis §N, actions `N3`–`N5`).
 *
 * Two switches, and neither one invents a new access mechanism.
 *
 * **Free is already modeled**: it's the ABSENCE of a payment obligation (no
 * `price_plan` in the course→program→cohort chain), which is exactly what
 * B1's entitlement calls `has_obligation = false`. A program, a cohort, or a
 * standalone class with no price plan are free with no one marking them, and
 * that's why there's NO "free mode" flag here: there'd be nothing to turn on.
 *
 * What DOES need a switch is what OPENS content or WRITES to the CRM:
 *
 * · `academy.free_preview` — open a lesson of a PAID course to the world. It's
 *   the only piece of B5 that widens who sees what, so it's born off and its
 *   gate is on the WRITE of `access_tier`, not on the read: the `free_preview`
 *   stream serves already-marked samples the same way the public catalog serves
 *   published courses. Trimming the stream by feature would break local-first
 *   without gaining any security (doctrine from `PLATFORM_CONTRACTS` §7).
 *
 * · `academy.free_funnel` — have the system log a lead when someone registers
 *   or enrolls in something free. It's automatic writing of another person's
 *   data in the CRM: off by default, and `requires: crm.leads` because
 *   without the lead-gen vertical the lead would have nowhere to live.
 */

import type { FeatureManifest } from "../country/packs";

const FREE_PREVIEW: FeatureManifest = {
  key: "academy.free_preview",
  name: "Clase muestra",
  description:
    "Permite marcar lecciones o módulos de un curso de pago como muestra gratuita. La muestra se replica a cualquier visitante autenticado —es material de marketing—, el resto del curso no.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Born OFF: it's the only piece of B5 that WIDENS who sees what.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "cursos",
  requires: [],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Marcar una lección o un módulo como clase muestra",
      "Publicar la muestra gratuita de un curso de pago",
    ],
    // Turning it off deletes nothing: already-marked lessons keep their
    // `access_tier`. What's prevented is marking more.
    dataPolicy: "preserved",
  },
};

const FREE_FUNNEL: FeatureManifest = {
  key: "academy.free_funnel",
  name: "Funnel automático de gratis a matrícula",
  description:
    "El registro abierto y la inscripción a algo gratuito crean un lead en el CRM con su origen, y la primera factura del alumno lo marca como convertido. Sirve para medir cuánta gente entró por el gancho gratis y cuánta acabó pagando.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Born OFF: it writes people's data to the CRM without anyone asking for it.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "crm",
  // HARD edge: a lead with no lead-gen vertical has nowhere to live and no
  // one to work it.
  requires: ["crm.leads"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Crear un lead automático cuando alguien se registra en abierto",
      "Crear un lead automático cuando alguien se inscribe a algo gratuito",
    ],
    dataPolicy: "preserved",
  },
};

/** "Free" manifests (source `free_tier`). */
export function freeTierManifests(): FeatureManifest[] {
  return [FREE_PREVIEW, FREE_FUNNEL];
}

/** Greppable keys from the client (mirror of `platform::features::keys`). */
export const FLAG_FREE_PREVIEW = FREE_PREVIEW.key;
export const FLAG_FREE_FUNNEL = FREE_FUNNEL.key;

/** `access_tier` vocabulary (mirror of migration 00090's CHECK). */
export const ACCESS_TIERS = ["paid", "free_preview"] as const;
export type AccessTier = (typeof ACCESS_TIERS)[number];

/** Is this lesson/module a sample class? */
export function isFreePreview(accessTier: string | null | undefined): boolean {
  return accessTier === "free_preview";
}
